/**
 * ConfirmationDialog Component
 * 
 * A reusable confirmation dialog component for delete and other confirmation actions.
 * Displays a warning message in Thai and provides confirm/cancel actions.
 * 
 * Accessibility Features:
 * - Proper ARIA labels and descriptions
 * - Focus management (auto-focus on cancel button for safety)
 * - Keyboard navigation support
 * - Screen reader announcements
 * 
 * Requirements: Policy Management Frontend - Task 5.4
 */

import React, { useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  Check as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

export interface ConfirmationDialogProps {
  /**
   * Whether the dialog is open
   */
  open: boolean;

  /**
   * Dialog title (in Thai)
   */
  title: string;

  /**
   * Main message to display (in Thai)
   */
  message: string;

  /**
   * Optional additional warning message (in Thai)
   */
  warningMessage?: string;

  /**
   * Text for the confirm button (default: "ยืนยัน")
   */
  confirmText?: string;

  /**
   * Text for the cancel button (default: "ยกเลิก")
   */
  cancelText?: string;

  /**
   * Color of the confirm button (default: "error" for delete actions)
   */
  confirmColor?: 'primary' | 'secondary' | 'error' | 'warning' | 'info' | 'success';

  /**
   * Whether the action is in progress (shows loading spinner)
   */
  loading?: boolean;

  /**
   * Loading text to show when action is in progress
   */
  loadingText?: string;

  /**
   * Icon to show on the confirm button (default: CheckIcon)
   */
  confirmIcon?: React.ReactNode;

  /**
   * Whether to disable the escape key and backdrop click
   */
  disableEscapeKeyDown?: boolean;

  /**
   * Callback when confirm button is clicked
   */
  onConfirm: () => void;

  /**
   * Callback when cancel button is clicked or dialog is closed
   */
  onCancel: () => void;
}

/**
 * ConfirmationDialog Component
 * 
 * A reusable dialog for confirming actions like deletion.
 * Provides consistent styling and behavior across the application.
 * 
 * @example
 * // Delete confirmation
 * <ConfirmationDialog
 *   open={deleteDialogOpen}
 *   title="ยืนยันการลบกรมธรรม์"
 *   message={`คุณต้องการลบกรมธรรม์ "${policy.policy_number}" ใช่หรือไม่?`}
 *   warningMessage="การดำเนินการนี้ไม่สามารถย้อนกลับได้"
 *   confirmText="ลบ"
 *   confirmColor="error"
 *   loading={deleting}
 *   loadingText="กำลังลบ..."
 *   onConfirm={handleDelete}
 *   onCancel={() => setDeleteDialogOpen(false)}
 * />
 * 
 * @example
 * // General confirmation
 * <ConfirmationDialog
 *   open={confirmDialogOpen}
 *   title="ยืนยันการดำเนินการ"
 *   message="คุณต้องการดำเนินการต่อใช่หรือไม่?"
 *   confirmText="ยืนยัน"
 *   confirmColor="primary"
 *   onConfirm={handleConfirm}
 *   onCancel={() => setConfirmDialogOpen(false)}
 * />
 */
const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  open,
  title,
  message,
  warningMessage,
  confirmText = 'ยืนยัน',
  cancelText = 'ยกเลิก',
  confirmColor = 'error',
  loading = false,
  loadingText,
  confirmIcon,
  disableEscapeKeyDown = false,
  onConfirm,
  onCancel,
}) => {
  const cancelButtonRef = useRef<HTMLButtonElement>(null);

  /**
   * Focus cancel button when dialog opens for safety
   */
  useEffect(() => {
    if (open && cancelButtonRef.current) {
      // Small delay to ensure dialog is fully rendered
      setTimeout(() => {
        cancelButtonRef.current?.focus();
      }, 100);
    }
  }, [open]);

  /**
   * Handle dialog close
   */
  const handleClose = () => {
    if (!loading) {
      onCancel();
    }
  };

  /**
   * Get default icon based on confirm color
   */
  const getDefaultIcon = () => {
    if (confirmIcon !== undefined) {
      return confirmIcon;
    }

    switch (confirmColor) {
      case 'error':
        return <DeleteIcon />;
      case 'warning':
        return <WarningIcon />;
      default:
        return <CheckIcon />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      disableEscapeKeyDown={disableEscapeKeyDown || loading}
      aria-labelledby="confirmation-dialog-title"
      aria-describedby="confirmation-dialog-description"
      aria-modal="true"
      role="alertdialog"
    >
      <DialogTitle id="confirmation-dialog-title">
        {title}
      </DialogTitle>

      <DialogContent>
        <DialogContentText id="confirmation-dialog-description">
          {message}
          {warningMessage && (
            <>
              <br />
              <br />
              <strong>{warningMessage}</strong>
            </>
          )}
        </DialogContentText>
      </DialogContent>

      <DialogActions>
        <Button
          ref={cancelButtonRef}
          onClick={handleClose}
          disabled={loading}
          startIcon={<CloseIcon />}
          aria-label={`${cancelText}การดำเนินการ`}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          color={confirmColor}
          variant="contained"
          disabled={loading}
          startIcon={
            loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              getDefaultIcon()
            )
          }
          aria-label={loading && loadingText ? loadingText : `${confirmText}การดำเนินการ`}
        >
          {loading && loadingText ? loadingText : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
